import { EXCHANGES, PAGE_SIZES } from '../../../constants';
import { translate } from '../../../helpers/translations/translator';
import { AppFilter } from '../../../redux/filter-ts/filterModels';
import {
    deleteExchangeApiCall,
    getExchangesApiCall,
    getShiftExchangesApiCall,
    patchApproveShiftExchangeApiCall,
    patchDeclineShiftExchangeApiCall,
    postExchangeLinkApiCall,
} from '../../entities/Exchange/ExchangeService';
import {
    FetchResultType,
    isFetchCollectionResultSuccessful,
    isFetchResultSuccessful,
} from '../../entities/FetchResult';
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../entities/NotificationCenter/NotificationCenter';
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '../../entities/NotificationCenter/NotificationCenterService';
import checkPermission from '../../helpers/permissions/checkPermission';
import { getCheckedFormOptionsValue } from '../../helpers/types/formOption';
import { ToastType } from '../../types';
import { fetchExchangeNotifications } from '../@interface/notifications/notificationsActions';
import { Reducers } from '../reducers';
import { TypedDispatch } from '../store';
import { setToast } from '../toasts/toastsReducer';
import {
    setError,
    setExchangeRequests,
    setIsLoading,
    setShiftExchanges,
} from './exchangesReducer';

export const fetchExchangeRequests = (
    filter: AppFilter,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch(setIsLoading(true));

    try {
        const state = getState();
        const { departmentOptions } = state.globalFiltersReducer;
        const departments = getCheckedFormOptionsValue(departmentOptions);
        const { permissions } = state.authenticatedUserReducer;

        const canViewSomeExchanges = checkPermission(permissions, 'view-own-departments-exchanges');
        const canViewSomeShiftExchanges = checkPermission(permissions, 'view-own-departments-shift-exchanges');

        const [exchangeRequestsResult, shiftExchangesResult] = await Promise.all([
            canViewSomeExchanges ? getExchangesApiCall(new Date(), {
                departments,
                onlyWithReplies: filter.absences.onlyWithReplies,
                employeeSearch: filter.employeeSearch,
            }, {
                number: 1,
                size: PAGE_SIZES.ABSENCES[EXCHANGES],
            }) : Promise.resolve({
                status: 200,
                type: FetchResultType.Success as const,
                data: [],
                amountOfPages: 1,
            }),
            canViewSomeShiftExchanges ? getShiftExchangesApiCall(new Date(), {
                departments,
                employeeSearch: filter.employeeSearch,
            }, {
                number: 1,
                size: PAGE_SIZES.ABSENCES[EXCHANGES],
            }) : Promise.resolve({
                status: 200,
                type: FetchResultType.Success as const,
                data: [],
                amountOfPages: 1,
            }),
        ]);

        if (isFetchCollectionResultSuccessful(exchangeRequestsResult)) {
            dispatch(setExchangeRequests(exchangeRequestsResult.data));
        } else {
            dispatch(setError(exchangeRequestsResult.error));
        }

        if (isFetchCollectionResultSuccessful(shiftExchangesResult)) {
            dispatch(setShiftExchanges(shiftExchangesResult.data));
        } else {
            dispatch(setError((shiftExchangesResult).error));
        }
    } catch (error) {
        console.error('[fetchExchangeRequests]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const approveExchangeRequest = (
    requestId: string,
    userId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true));

    try {
        const responseTracker = await startEventLoggerApiCall(
            NotificationCenterCategory.EXCHANGE,
            NotificationCenterAction.DELETED,
            [NotificationCenterActionCategory.EXCHANGE_APPROVED],
        );

        if (!isFetchResultSuccessful(responseTracker)) {
            dispatch(setError(responseTracker.error));
            return;
        }

        try {
            const result = await postExchangeLinkApiCall(requestId, userId, responseTracker.data);

            if (!isFetchResultSuccessful(result)) {
                dispatch(setError(result.error));

                return;
            }

            dispatch(setToast({
                text: translate('pages.absences.approveExchangeSuccess'),
                type: ToastType.pass,
                shouldFadeOutAutomatically: true,
            }));
            dispatch(fetchExchangeNotifications());
        } finally {
            await endEventLoggerApiCall(responseTracker.data);
        }
    } catch (error) {
        console.error('[approveExchangeRequest]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const declineExchangeRequest = (
    requestId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true));

    try {
        const responseTracker = await startEventLoggerApiCall(
            NotificationCenterCategory.EXCHANGE,
            NotificationCenterAction.DELETED,
            [NotificationCenterActionCategory.EXCHANGE_DECLINED],
        );

        if (!isFetchResultSuccessful(responseTracker)) {
            dispatch(setError(responseTracker.error));
            return;
        }

        try {
            const result = await deleteExchangeApiCall(requestId, responseTracker.data);

            if (!isFetchResultSuccessful(result)) {
                dispatch(setError(result.error));

                return;
            }

            dispatch(setToast({
                text: translate('pages.absences.declineExchangeSuccess'),
                type: ToastType.pass,
                shouldFadeOutAutomatically: true,
            }));
            dispatch(fetchExchangeNotifications());
        } finally {
            await endEventLoggerApiCall(responseTracker.data);
        }
    } catch (error) {
        console.error('[declineExchangeRequest]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const approveShiftExchange = (
    shiftExchangeId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true));

    try {
        const responseTracker = await startEventLoggerApiCall(
            NotificationCenterCategory.SHIFT_EXCHANGE,
            NotificationCenterAction.DELETED,
            [NotificationCenterActionCategory.SHIFT_EXCHANGE_APPROVED],
        );

        if (!isFetchResultSuccessful(responseTracker)) {
            dispatch(setError(responseTracker.error));
            return;
        }

        try {
            const result = await patchApproveShiftExchangeApiCall(shiftExchangeId, responseTracker.data);

            if (!isFetchResultSuccessful(result)) {
                dispatch(setError(result.error));

                return;
            }

            dispatch(setToast({
                text: translate('pages.absences.approveExchangeSuccess'),
                type: ToastType.pass,
                shouldFadeOutAutomatically: true,
            }));
            dispatch(fetchExchangeNotifications());
        } finally {
            await endEventLoggerApiCall(responseTracker.data);
        }
    } catch (error) {
        console.error('[approveShiftExchange]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const declineShiftExchange = (
    shiftExchangeId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true));

    try {
        const responseTracker = await startEventLoggerApiCall(
            NotificationCenterCategory.SHIFT_EXCHANGE,
            NotificationCenterAction.UPDATED,
            [NotificationCenterActionCategory.SHIFT_EXCHANGE_DECLINED],
        );

        if (!isFetchResultSuccessful(responseTracker)) {
            dispatch(setError(responseTracker.error));
            return;
        }

        try {
            const result = await patchDeclineShiftExchangeApiCall(shiftExchangeId, responseTracker.data);

            if (!isFetchResultSuccessful(result)) {
                dispatch(setError(result.error));

                return;
            }

            dispatch(setToast({
                text: translate('pages.absences.declineExchangeSuccess'),
                type: ToastType.pass,
                shouldFadeOutAutomatically: true,
            }));
            dispatch(fetchExchangeNotifications());
        } finally {
            await endEventLoggerApiCall(responseTracker.data);
        }
    } catch (error) {
        console.error('[declineShiftExchange]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
